import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, History, ExternalLink } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { StatusPill } from "@/components/LogSection";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const SCOPES = [
  { key: "mine", label: "My Approvals" },
  { key: "pending", label: "All Pending" },
  { key: "all", label: "Full History" },
];

export default function Approvals() {
  const navigate = useNavigate();
  const [scope, setScope] = useState("mine");
  const [rows, setRows] = useState(null);
  const [decision, setDecision] = useState(null);
  const [comments, setComments] = useState("");
  const [trail, setTrail] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    const qs = scope === "mine" ? "?mine=true" : scope === "pending" ? "?status=Pending" : "";
    try {
      const { data } = await api.get(`/approvals${qs}`);
      setRows(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setRows([]);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const decide = async (action) => {
    try {
      const { data } = await api.post(`/approvals/${decision.id}/decide`, { action, comments: comments || null });
      toast.success(
        data.item_outcome
          ? `${action} — ${decision.approval_type} is now ${data.item_outcome}`
          : `${action} — awaiting the other approvers`,
      );
      setDecision(null);
      setComments("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openTrail = async (r) => {
    try {
      const { data } = await api.get(`/approvals/items/${r.item_id}/trail`);
      setTrail({ row: r, events: data });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Shell
      testId="approvals-page"
      title="Approvals"
      subtitle="Stage gate, change request, decision and risk acceptance approvals routed to the configured approvers"
    >
      <div className="mb-5 flex gap-1 border-b border-slate-200">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            data-testid={`approvals-scope-${s.key}`}
            onClick={() => setScope(s.key)}
            className={`relative px-4 py-2 text-sm font-medium transition-colors ${scope === s.key ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
          >
            {s.label}
            {scope === s.key && <span className="absolute inset-x-2 -bottom-px h-0.5" style={{ backgroundColor: "var(--brand-primary)" }} />}
          </button>
        ))}
      </div>

      {!rows ? (
        <Empty text="Loading approvals…" />
      ) : rows.length === 0 ? (
        <Empty testId="approvals-empty" text="Nothing in this queue." />
      ) : (
        <div className="panel overflow-x-auto" data-testid="approvals-table">
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Type", "Item", "Project", "Level", "Approver", "Requested", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`approval-row-${r.id}`} className="transition-colors hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-600">{r.approval_type}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {r.item_ref ? <span className="mr-1.5 text-xs text-slate-400">{r.item_ref}</span> : null}
                    {r.item_title || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`approval-open-project-${r.id}`}
                      onClick={() => navigate(`/projects/${r.project_id}`)}
                      className="inline-flex items-center gap-1 text-slate-700 underline-offset-2 hover:underline"
                    >
                      {r.project_name} <ExternalLink size={12} className="text-slate-400" />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.level}</td>
                  <td className="px-4 py-3 text-slate-600">{r.approver_name}</td>
                  <td className="px-4 py-3 text-slate-500">{r.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3"><StatusPill value={r.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`approval-trail-${r.id}`} onClick={() => openTrail(r)} title="Audit trail" aria-label="Audit trail" className="text-slate-400 hover:text-slate-700">
                        <History size={15} />
                      </button>
                      {r.status === "Pending" && (
                        <Button size="sm" data-testid={`approval-decide-${r.id}`} onClick={() => { setDecision(r); setComments(""); }} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
                          Review
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent data-testid="approval-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{decision?.approval_type} Approval</DialogTitle>
            <DialogDescription>
              {decision?.item_title} · {decision?.project_name} · {decision?.level}
              {decision?.threshold ? ` · threshold ${decision.threshold}` : ""}
            </DialogDescription>
          </DialogHeader>
          {decision?.note && <p className="rounded bg-slate-50 p-3 text-sm text-slate-600">Requester note: {decision.note}</p>}
          <div>
            <label className="field-label">Approval Comments</label>
            <Textarea data-testid="approval-comments-input" rows={3} value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Optional, but recorded on the audit trail" />
          </div>
          <DialogFooter className="sm:justify-end">
            <Button variant="outline" data-testid="approval-reject-btn" onClick={() => decide("Rejected")} className="gap-1.5 text-red-600">
              <X size={14} /> Reject
            </Button>
            <Button data-testid="approval-approve-btn" onClick={() => decide("Approved")} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Check size={14} /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!trail} onOpenChange={(o) => !o && setTrail(null)}>
        <DialogContent data-testid="approval-trail-dialog" className="max-h-[80vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Trail</DialogTitle>
            <DialogDescription>{trail?.row?.item_title}</DialogDescription>
          </DialogHeader>
          {trail?.events?.length ? (
            <ol className="space-y-3 border-l border-slate-200 pl-4">
              {trail.events.map((e) => (
                <li key={e.id} data-testid={`trail-event-${e.id}`} className="relative text-sm">
                  <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
                  <div className="font-medium text-slate-800">{e.action}</div>
                  <div className="text-xs text-slate-500">{e.actor} · {e.at?.slice(0, 16).replace("T", " ")}</div>
                  {e.detail && <p className="mt-0.5 text-slate-600">{e.detail}</p>}
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-slate-500">No events recorded.</p>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
